import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import "./ProjectsList.css";
import { getProjectPortfolio, exportProjectPersonHours, getAllProjectsSummary } from "../../../api/projectApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import AddUserIcon from "../../../images/projects/ProjectFinance/People.svg";

const regularColumns = [
  { key: "contract_year", label: "Период договора" },
  { key: "building_type", label: "Вид здания", filter: true },
  { key: "cipher_pilot", label: "№ заказа", filter: true },
  { key: "name", label: "Наименование объекта" },
  { key: "chief", label: "ГИП проекта", filter: true },
  { key: "status", label: "Стадия", filter: true },
];

const financeColumns = [
  { key: "code", label: "№ заказа", filter: true },
  { key: "chief", label: "ГИП проекта", filter: true },
  { key: "cipher", label: "Шифр проекта" },
  { key: "status", label: "Стадия", filter: true },
  { key: "contract_number", label: "Номер договора" },
  { key: "amount", label: "Сумма" },
  { key: "design_start", label: "Начало проектирования" },
  { key: "expertise_start", label: "Начало экспертизы" },
  { key: "expertise_end", label: "Конец экспертизы" },
  { key: "project_end", label: "Окончание проекта" },
];

const sortProjects = (arr) => {
  return [...arr].sort((a, b) => {
    // Сортируем по contract_year (по убыванию)
    const yearA = a.contract_year ? parseInt(a.contract_year) : 0;
    const yearB = b.contract_year ? parseInt(b.contract_year) : 0;
    if (yearA !== yearB) return yearB - yearA;
    // Если года равны — по id (по убыванию)
    return (b.id || 0) - (a.id || 0);
  });
};

const getUnique = (arr, key, isChief = false) => {
  const set = new Set();
  arr.forEach((item) => {
    if (isChief) {
      if (item.chief)
        set.add(`${item.chief.name} ${item.chief.surname}`.trim());
    } else {
      if (item[key]) set.add(item[key]);
    }
  });
  return Array.from(set).filter(Boolean);
};

const ProjectsList = () => {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();

  // Определяем роли пользователя
  const isAdmin = userData?.access_levels?.some(
    (level) => level.name === "АДМС" || level.name === "АДМП"
  );
  const isFinance = userData?.access_levels?.some(
    (level) => level.name === "ФИН"
  );
  const hasProjectFinanceAccess =
    userData &&
    ([94, 96, 98, 99].includes(userData.id) ||
      (userData.access_levels &&
        userData.access_levels.some(
          (level) =>
            level.name === "АДМС" ||
            level.name === "АДМП" ||
            level.name === "ГИП" ||
            level.name === "РО" ||
            level.name === "ФИН"
        )));

  // Выбираем колонки в зависимости от уровня доступа
  const columns = isFinance ? financeColumns : regularColumns;

  // Определяем, нужно ли показывать колонку действий
  const showActionsColumn = isAdmin;

  // Определяем, должны ли быть кликабельными строки
  const isRowClickable = isAdmin || hasProjectFinanceAccess;

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    building_type: [],
    chief: [],
    status: [],
    code: [],
    cipher_pilot: [],
  });
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportProjects, setExportProjects] = useState([]);
  const [selectedExportProject, setSelectedExportProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Стили для react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "100%",
      minHeight: "auto",
      boxShadow: "none",
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      marginTop: "5px",
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: "#E0E0E0",
      borderRadius: "5px",
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      fontSize: "12px",
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: "#666",
      ":hover": {
        backgroundColor: "#ccc",
        color: "#333",
      },
    }),
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError(null);
      try {
        // Используем новый быстрый эндпоинт для получения портфолио проектов
        const portfolioData = await getProjectPortfolio();
        setProjects(
          Array.isArray(portfolioData) ? sortProjects(portfolioData) : []
        );
      } catch (e) {
        console.error("Ошибка при загрузке портфолио проектов:", e);
        setError("Ошибка при загрузке проектов");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Логика для фиксированного скроллбара
  useEffect(() => {
    if (!isFinance) return; // Только для финансистов

    const tableWrapper = document.querySelector(".portfolio-table-wrapper");
    const createFixedScrollbar = () => {
      // Удаляем существующий скроллбар
      const existingScrollbar = document.querySelector(
        ".fixed-horizontal-scrollbar"
      );
      if (existingScrollbar) {
        existingScrollbar.remove();
      }

      // Создаем новый скроллбар
      const scrollbar = document.createElement("div");
      scrollbar.className = "fixed-horizontal-scrollbar";

      const thumb = document.createElement("div");
      thumb.className = "fixed-scrollbar-thumb";

      scrollbar.appendChild(thumb);
      document.body.appendChild(scrollbar);

      // Вычисляем размеры
      const updateScrollbar = () => {
        if (!tableWrapper) return;

        const scrollWidth = tableWrapper.scrollWidth;
        const clientWidth = tableWrapper.clientWidth;
        const scrollLeft = tableWrapper.scrollLeft;

        if (scrollWidth > clientWidth) {
          scrollbar.style.display = "block";
          const thumbWidth = (clientWidth / scrollWidth) * clientWidth;
          const thumbLeft = (scrollLeft / scrollWidth) * clientWidth;

          thumb.style.width = `${thumbWidth}px`;
          thumb.style.marginLeft = `${thumbLeft}px`;
        } else {
          scrollbar.style.display = "none";
        }
      };

      // Обработчики событий
      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;

      const onMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault(); // Предотвращаем скролл страницы и выделение

        const deltaX = e.clientX - startX;
        const tableScrollRange =
          tableWrapper.scrollWidth - tableWrapper.clientWidth;
        const thumbScrollRange = scrollbar.clientWidth - thumb.clientWidth;

        if (thumbScrollRange > 0) {
          tableWrapper.scrollLeft =
            startScrollLeft + (deltaX * tableScrollRange) / thumbScrollRange;
        }
      };

      const onMouseUp = () => {
        isDragging = false;
        document.body.classList.remove("no-select");
        thumb.style.cursor = "grab";

        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      const onMouseDown = (e) => {
        e.preventDefault(); // Предотвращаем выделение текста
        isDragging = true;
        startX = e.clientX;
        startScrollLeft = tableWrapper.scrollLeft;

        document.body.classList.add("no-select");
        thumb.style.cursor = "grabbing";

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      };

      thumb.addEventListener("mousedown", onMouseDown);

      // Обновляем при скролле таблицы
      tableWrapper.addEventListener("scroll", updateScrollbar);

      // Начальное обновление
      updateScrollbar();

      // Очистка при размонтировании
      return () => {
        tableWrapper.removeEventListener("scroll", updateScrollbar);
        thumb.removeEventListener("mousedown", onMouseDown);
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.classList.remove("no-select"); // На случай размонтирования во время перетаскивания
        if (scrollbar) {
          scrollbar.remove();
        }
      };
    };

    // Создаем скроллбар после загрузки данных
    if (!loading && projects.length > 0) {
      const cleanup = createFixedScrollbar();
      return cleanup;
    }
  }, [isFinance, loading, projects.length]);

  // Получаем уникальные значения для фильтров
  const buildingTypes = getUnique(projects, "building_type");
  const chiefs = getUnique(projects, "chief", true);
  const statuses = getUnique(projects, "status");
  const cipherPilots = getUnique(projects, "cipher_pilot");

  // Фильтрация
  const filteredProjects = projects.filter((p) => {
    if (isFinance) {
      // Для финансистов фильтры по коду проекта, ГИПу и статусу
      const codeMatch =
        filters.code.length === 0 || filters.code.includes(p.cipher_pilot);
      const chiefMatch =
        filters.chief.length === 0 ||
        (p.chief &&
          filters.chief.includes(`${p.chief.name} ${p.chief.surname}`.trim()));
      const statusMatch =
        filters.status.length === 0 || filters.status.includes(p.status);
      return codeMatch && chiefMatch && statusMatch;
    } else {
      // Для обычных пользователей все фильтры
      const buildingTypeMatch =
        filters.building_type.length === 0 ||
        filters.building_type.includes(p.building_type);
      const chiefMatch =
        filters.chief.length === 0 ||
        (p.chief &&
          filters.chief.includes(`${p.chief.name} ${p.chief.surname}`.trim()));
      const statusMatch =
        filters.status.length === 0 || filters.status.includes(p.status);
      const cipherPilotMatch =
        filters.cipher_pilot.length === 0 ||
        filters.cipher_pilot.includes(p.cipher_pilot);
      return buildingTypeMatch && chiefMatch && statusMatch && cipherPilotMatch;
    }
  });

  // Функция для обработки изменений фильтров
  const handleFilterChange = (column, selectedOptions) => {
    setFilters((prev) => ({
      ...prev,
      [column]: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  // Функция для отображения данных в зависимости от колонки
  const renderCellData = (project, columnKey) => {
    switch (columnKey) {
      case "code":
        return project.cipher_pilot || "—";
      case "cipher":
        return project.cipher_fin || "—";
      case "contract_number":
        return project.contract_number || "—";
      case "amount":
        return project.contract_amount ? `${project.contract_amount} ₸` : "—";
      case "design_start":
        return project.date_start || "—";
      case "expertise_start":
        return project.expert_start || "—";
      case "expertise_end":
        return project.expert_end || "—";
      case "project_end":
        return project.date_end || "—";
      case "contract_year":
        return project.contract_year || "—";
      case "building_type":
        return project.building_type || "—";
      case "cipher_pilot":
        return project.cipher_pilot || "—";
      case "name":
        return project.name || "—";
      case "chief":
        return project.chief
          ? `${project.chief.name || ""} ${project.chief.surname || ""}`.trim()
          : "—";
      case "status":
        return project.status || "—";
      default:
        return "—";
    }
  };

  // Загрузка списка проектов для выгрузки
  useEffect(() => {
    if (showExportModal) {
      const loadProjects = async () => {
        setLoadingProjects(true);
        try {
          const projects = await getAllProjectsSummary();
          const options = (projects || []).map((p) => ({
            value: p.id,
            label: `${p.code ? p.code + " — " : ""}${p.name || "Без названия"}`,
            cipher_pilot: p.code || p.cipher_pilot,
          }));
          setExportProjects(options);
        } catch (error) {
          console.error("Ошибка при загрузке проектов:", error);
          alert("Не удалось загрузить список проектов");
        } finally {
          setLoadingProjects(false);
        }
      };
      loadProjects();
    }
  }, [showExportModal]);

  // Обработчик открытия модального окна выгрузки
  const handleOpenExportModal = () => {
    setShowExportModal(true);
    setSelectedExportProject(null);
  };

  // Обработчик закрытия модального окна
  const handleCloseExportModal = () => {
    setShowExportModal(false);
    setSelectedExportProject(null);
  };

  // Обработчик выгрузки трудозатрат
  const handleExportPersonHours = async () => {
    if (!selectedExportProject) {
      alert("Пожалуйста, выберите проект");
      return;
    }

    setIsExporting(true);
    try {
      // Используем "АН" как значение по умолчанию для типа отчета
      await exportProjectPersonHours(selectedExportProject.value);
      handleCloseExportModal();
    } catch (error) {
      console.error("Ошибка при выгрузке трудозатрат:", error);
      alert(error.message || "Произошла ошибка при выгрузке трудозатрат");
    } finally {
      setIsExporting(false);
    }
  };

  // Обработчик клика по строке
  const handleRowClick = (project, e) => {
    // Не обрабатываем клики по кнопкам действий и ячейке шифра
    if (
      e.target.closest(".portfolio-action-btn") ||
      e.target.closest('td[data-column="cipher"]')
    ) {
      return;
    }

    // Для всех пользователей с доступом открываем финансовую часть проекта
    if (isAdmin || hasProjectFinanceAccess) {
      // Проверяем department_id для определения типа проекта
      // Если department_id === 4, то это проект Эскизного Проектирования
      // Проверяем несколько возможных мест, где может быть department_id
      const departmentId =
        project.department_id ||
        project.project_type?.department_id ||
        (project.departments && project.departments[0]?.id);

      // Также проверяем по названию проекта (если содержит "_ЭП_" или заканчивается на "_ЭП")
      const nameContainsEP =
        project.name &&
        (project.name.includes("_ЭП_") ||
          project.name.includes(" ЭП ") ||
          project.name.endsWith("_ЭП"));

      const isSketchDesign = departmentId === 4 || nameContainsEP;

      if (isSketchDesign) {
        // Перенаправляем на страницы финансов ЭП проектов
        navigate(`/projects/sketch-design/finance/${project.id}`);
      } else {
        // Перенаправляем на страницы финансов РП проектов
        navigate(`/projects/finance/${project.id}`);
      }
    }
  };

  return (
    <div className="portfolio-container">
      <div className="portfolio-header">
        <h1>Портфель проектов</h1>
        {isAdmin && (
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              className="portfolio-export-btn"
              onClick={handleOpenExportModal}
              disabled={isExporting}
            >
              Выгрузить трудозатраты
            </button>
            <button
              className="portfolio-create-btn"
              onClick={() => navigate("/projects/add")}
            >
              Создать проект
            </button>
          </div>
        )}
      </div>
      <div className="portfolio-table-wrapper">
        {loading ? (
          <div style={{ padding: 24 }}>Загрузка...</div>
        ) : error ? (
          <div style={{ color: "red", padding: 24 }}>{error}</div>
        ) : (
          <table className="portfolio-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className={col.filter ? "th-filter" : ""}>
                    {col.filter ? (
                      <Select
                        isMulti
                        className="portfolio-th-select"
                        value={filters[col.key].map((value) => ({
                          value,
                          label: value,
                        }))}
                        onChange={(selectedOptions) =>
                          handleFilterChange(col.key, selectedOptions)
                        }
                        options={
                          isFinance && col.key === "code"
                            ? cipherPilots.map((v) => ({
                                value: v,
                                label: v,
                              }))
                            : isFinance && col.key === "chief"
                            ? chiefs.map((v) => ({
                                value: v,
                                label: v,
                              }))
                            : !isFinance && col.key === "cipher_pilot"
                            ? cipherPilots.map((v) => ({
                                value: v,
                                label: v,
                              }))
                            : !isFinance && col.key === "building_type"
                            ? buildingTypes.map((v) => ({
                                value: v,
                                label: v,
                              }))
                            : !isFinance && col.key === "chief"
                            ? chiefs.map((v) => ({
                                value: v,
                                label: v,
                              }))
                            : col.key === "status"
                            ? statuses.map((v) => ({
                                value: v,
                                label: v,
                              }))
                            : []
                        }
                        styles={customStyles}
                        placeholder={col.label}
                        noOptionsMessage={() => "Нет доступных опций"}
                        menuPortalTarget={document.body}
                      />
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
                {showActionsColumn && <th>Действия</th>}
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((p) => (
                <tr
                  key={p.id}
                  className={`portfolio-table-row ${
                    isRowClickable ? "clickable" : ""
                  }`}
                  onClick={(e) => handleRowClick(p, e)}
                >
                  {columns.map((col) => (
                    <td key={col.key} data-column={col.key}>
                      {renderCellData(p, col.key)}
                    </td>
                  ))}
                  {showActionsColumn && (
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        {isAdmin && (
                          <>
                            <button
                              className="portfolio-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/edit/${p.id}`);
                              }}
                              title="Редактировать проект"
                            >
                              ✏️
                            </button>
                            <button
                              className="portfolio-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/teams/${p.id}`);
                              }}
                              title="Редактировать команду проекта"
                            >
                              <img
                                src={AddUserIcon}
                                alt="Команда"
                                style={{ width: "28px", height: "28px" }}
                              />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Модальное окно выгрузки трудозатрат */}
      {showExportModal && (
        <>
          <div
            className="export-modal-backdrop"
            onClick={handleCloseExportModal}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          />
          <div
            className="export-modal"
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              minWidth: "500px",
              maxWidth: "600px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              zIndex: 1001,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                margin: "0 0 20px 0",
                fontSize: "20px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              Выгрузка трудозатрат проекта
            </h2>

            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Выберите проект *
              </label>
              <Select
                styles={customStyles}
                options={exportProjects}
                value={selectedExportProject}
                onChange={setSelectedExportProject}
                placeholder="Начните вводить название или шифр проекта..."
                isLoading={loadingProjects}
                isClearable
                noOptionsMessage={() => "Нет доступных проектов"}
                menuPortalTarget={document.body}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleCloseExportModal}
                disabled={isExporting}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: isExporting ? "not-allowed" : "pointer",
                  opacity: isExporting ? 0.6 : 1,
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleExportPersonHours}
                disabled={isExporting || !selectedExportProject}
                style={{
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: isExporting || !selectedExportProject ? "#ccc" : "#2a5e38",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: isExporting || !selectedExportProject ? "not-allowed" : "pointer",
                  transition: "background 0.2s",
                }}
              >
                {isExporting ? "Выгрузка..." : "Выгрузить"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectsList;
